import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { httpGet } from 'utils';

interface HoldingsSummaryItem {
  acctHolderId: number;
  portfolioId: number;
  currentHoldingValue: number;
}

interface HoldingsSummaryResponse {
  data: HoldingsSummaryItem[];
  size: number;
}

const fetchHoldingsSummary = () =>
  httpGet<HoldingsSummaryResponse>('portal/PortalDocument?docType=portal_current_holdings_summary');

interface PortfolioHoldingsTotals {
  totalsByPortfolioId: Record<string, number>;
  grandTotal: number;
  isLoading: boolean;
  isUpdating: boolean;
}

export const usePortfolioHoldingsTotals = (): PortfolioHoldingsTotals => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['portalCurrentHoldingsSummary'],
    queryFn: fetchHoldingsSummary,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const { totalsByPortfolioId, grandTotal } = useMemo(() => {
    const totals: Record<string, number> = {};
    let grand = 0;

    (data?.data ?? []).forEach((item) => {
      totals[String(item.portfolioId)] = item.currentHoldingValue;
      grand += item.currentHoldingValue;
    });

    return { totalsByPortfolioId: totals, grandTotal: grand };
  }, [data]);

  return {
    totalsByPortfolioId,
    grandTotal,
    isLoading,
    isUpdating: isFetching && !isLoading,
  };
};
